import { clamp } from '../units/round.js';
import { SAFETY_LIMITS } from '../safety/limits.js';
import type { PerformedSetSummary, RepTarget } from './types.js';

/**
 * Ajuste intra-sesion (docs/LOAD_RECOMMENDATION_ENGINE.md §5).
 * La clave es la BANDA MUERTA: no se sobrerreacciona a diferencias normales.
 */

export interface Deviation {
  /** Desviacion en repeticiones equivalentes. */
  value: number;
  repsDelta: number;
  rirDelta: number;
}

/**
 * Convierte reps y RIR a una unidad comun: la CAPACIDAD estimada de la serie,
 * repeticiones hechas más las que quedaban en reserva.
 *
 * `dev = (reps − repsObjetivo) + (rir − rirObjetivo)`
 *
 * Ejemplo: objetivo 10 @ RIR 2 (capacidad 12), realizado 8 @ RIR 0
 * (capacidad 8) -> dev = −2 + (−2) = −4: rindió claramente por debajo.
 *
 * HASTA EL 23/09/2026 EL SIGNO DEL RIR ESTABA AL REVÉS (`rirObjetivo − rir`), y
 * este mismo comentario decía que ese ejemplo era «el esfuerzo previsto». Con
 * el signo invertido, menos reserva contaba como mejor rendimiento: 10 @ RIR 0
 * daba +2 y SUBÍA la carga de la serie siguiente, y 10 @ RIR 4 daba −2 y la
 * bajaba. A igualdad de repeticiones, más RIR es más reserva, no peor serie.
 *
 * Lo que esta corrección NO demuestra: que sumar repeticiones y RIR, o los
 * porcentajes de `adjustmentForDeviation`, sean una política óptima. Son una
 * heurística del producto —el RIR declarado es una estimación con error de ±1
 * o más—, y la banda muerta existe precisamente por eso.
 */
export function computeDeviation(target: RepTarget, performed: PerformedSetSummary): Deviation {
  const targetReps = (target.repsMin + target.repsMax) / 2;
  const repsDelta = performed.reps - targetReps;
  const rirDelta = performed.rir - target.rir;
  return { value: repsDelta + rirDelta, repsDelta, rirDelta };
}

export interface IntraSessionAdjustment {
  /** Factor a aplicar a la carga de la serie siguiente (1 = sin cambio). */
  factor: number;
  reasonCodes: string[];
}

/** Banda muerta y respuesta escalonada. */
export function adjustmentForDeviation(deviation: number): IntraSessionAdjustment {
  const dev = deviation;

  if (dev >= -1 && dev <= 1) return { factor: 1, reasonCodes: ['within_dead_band'] };
  if (dev <= -4) return { factor: 0.925, reasonCodes: ['well_below_target'] };
  if (dev <= -2) return { factor: 0.95, reasonCodes: ['below_target'] };
  if (dev < -1) return { factor: 0.975, reasonCodes: ['slightly_below_target'] };
  if (dev >= 2) return { factor: 1.05, reasonCodes: ['above_target'] };
  return { factor: 1.025, reasonCodes: ['slightly_above_target'] };
}

export interface IntraSessionInput {
  /** Serie que se acaba de registrar y su objetivo. */
  lastSet: PerformedSetSummary;
  lastTarget: RepTarget;
  /** Carga de la PRIMERA serie del ejercicio hoy, para acotar el acumulado. */
  firstSetLoadKg: number;
  /** Carga con la que se iba a afrontar la serie siguiente. */
  plannedNextLoadKg: number;
  /** Indice de la serie siguiente y total de series del ejercicio. */
  nextSetIndex: number;
  totalSets: number;
  /** Señales que refuerzan una bajada, nunca una subida. */
  readinessScore: number | null;
  regionSoreness: number | null;
}

export interface IntraSessionResult {
  loadKg: number;
  factor: number;
  reasonCodes: string[];
}

/**
 * Nueva carga para la serie siguiente. El resultado todavia debe redondearse a
 * una carga seleccionable: esta funcion es pura aritmetica de dominio.
 */
export function adjustNextSet(input: IntraSessionInput): IntraSessionResult {
  const deviation = computeDeviation(input.lastTarget, input.lastSet);
  const base = adjustmentForDeviation(deviation.value);
  const reasonCodes = [...base.reasonCodes];
  let factor = base.factor;

  // En la ultima serie del ejercicio no se sube: se anota para la proxima sesion.
  const isLastSet = input.nextSetIndex >= input.totalSets;
  if (factor > 1 && isLastSet) {
    factor = 1;
    reasonCodes.push('no_increase_on_last_set');
  }

  // El readiness bajo y las agujetas refuerzan una bajada, nunca una subida.
  const lowReadiness = input.readinessScore !== null && input.readinessScore < 50;
  const highSoreness = input.regionSoreness !== null && input.regionSoreness >= 4;
  if (factor > 1 && (lowReadiness || highSoreness)) {
    factor = 1;
    reasonCodes.push(lowReadiness ? 'increase_blocked_low_readiness' : 'increase_blocked_soreness');
  }
  if (factor < 1 && (lowReadiness || highSoreness)) {
    factor = Math.min(factor, factor - 0.025);
    reasonCodes.push('decrease_reinforced');
  }

  const target = input.plannedNextLoadKg * factor;

  // Tope acumulado respecto a la primera serie del ejercicio.
  const maxChange = SAFETY_LIMITS.load.maxIntraSessionChangePct;
  const bounded = clamp(
    target,
    input.firstSetLoadKg * (1 - maxChange),
    input.firstSetLoadKg * (1 + maxChange),
  );
  if (bounded !== target) reasonCodes.push('clamped_intra_session_cumulative');

  return {
    loadKg: bounded,
    factor: input.plannedNextLoadKg === 0 ? 1 : bounded / input.plannedNextLoadKg,
    reasonCodes,
  };
}
